import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, Star } from "lucide-react";
import { useFeltFavorites, useFeltRecents } from "@/lib/felt-storage";

export const Route = createFileRoute("/felt/")({
  head: () => ({
    meta: [
      { title: "Feltmodus – beregninger | VERQO" },
      {
        name: "description",
        content:
          "Søk opp fagberegninger i VERQO feltmodus: grovsjekk av kurs, spenningsfall og maks kabellengde.",
      },
      { property: "og:title", content: "Feltmodus – beregninger" },
      {
        property: "og:description",
        content: "Søk opp fagberegninger på byggeplass i VERQO feltmodus.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: FeltIndex,
});

interface FeltEntry {
  to: string;
  title: string;
  subtitle: string;
  keywords: string;
}

const ENTRIES: FeltEntry[] = [
  {
    to: "/felt/foto",
    title: "Bilder til befaring",
    subtitle: "Ta bilde på plassen, knytt til prosjekt og legg ved notat",
    keywords: "bilde foto kamera befaring dokumentasjon notat prosjekt",
  },
  {
    to: "/felt/kurs",
    title: "Grovsjekk kurs",
    subtitle: "Spenningsfall, Ib ≤ In ≤ Iz og maks kabellengde på én skjerm",
    keywords: "kurs sikring vern spenningsfall kabel iz ib in grovsjekk",
  },
  {
    to: "/felt/vegg",
    title: "Vegg – materialer",
    subtitle: "Stendere, isolasjon, dampsperre og gips fra lengde × høyde",
    keywords: "vegg stender bindingsverk isolasjon dampsperre gips plater tømrer materialer",
  },
  {
    to: "/calculations/voltage-drop",
    title: "Spenningsfall",
    subtitle: "ΔU i volt og prosent",
    keywords: "spenningsfall volt prosent kabel",
  },
  {
    to: "/calculations/cable",
    title: "Kabeldimensjonering",
    subtitle: "Tverrsnitt og strømføringsevne",
    keywords: "kabel tverrsnitt dimensjonering",
  },
  {
    to: "/calculations/ohms",
    title: "Ohms lov",
    subtitle: "U, I, R og P",
    keywords: "ohm strøm spenning motstand effekt",
  },
];

function formatWhen(at: number) {
  return new Date(at).toLocaleString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function FeltIndex() {
  const [query, setQuery] = useState("");
  const { isFavorite, toggle } = useFeltFavorites();
  const { recents, clear } = useFeltRecents();

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = !q
      ? ENTRIES
      : ENTRIES.filter(
          (e) =>
            e.title.toLowerCase().includes(q) ||
            e.subtitle.toLowerCase().includes(q) ||
            e.keywords.includes(q),
        );
    return [...list].sort(
      (a, b) => Number(isFavorite(b.to)) - Number(isFavorite(a.to)),
    );
  }, [query, isFavorite]);

  return (
    <>
      <h1 className="sr-only">Feltmodus</h1>
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          className="input-base h-12 w-full pl-11 text-base"
          placeholder="Søk etter beregning"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {recents.length > 0 && !query && (
        <section className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Siste beregninger
            </h2>
            <button
              type="button"
              onClick={clear}
              className="h-12 px-2 text-sm text-muted-foreground underline-offset-2 hover:underline"
            >
              Tøm
            </button>
          </div>
          <ul className="space-y-3">
            {recents.map((r) => (
              <li key={r.to}>
                <Link
                  to={r.to}
                  className="card flex min-h-[72px] flex-col justify-center px-4 py-3 transition-colors hover:bg-muted/50"
                >
                  <span className="text-base font-semibold">{r.title}</span>
                  <span className="text-sm text-muted-foreground">{r.summary}</span>
                  <span className="text-xs text-muted-foreground">{formatWhen(r.at)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <ul className="space-y-3">
        {visible.map((e) => (
          <li key={e.to} className="relative">
            <Link
              to={e.to}
              className="card flex min-h-[72px] flex-col justify-center px-4 py-3 pr-16 transition-colors hover:bg-muted/50"
            >
              <span className="text-lg font-semibold">{e.title}</span>
              <span className="text-sm text-muted-foreground">{e.subtitle}</span>
            </Link>
            <button
              type="button"
              aria-label={
                isFavorite(e.to)
                  ? `Fjern ${e.title} fra favoritter`
                  : `Legg ${e.title} til favoritter`
              }
              aria-pressed={isFavorite(e.to)}
              onClick={() => toggle(e.to)}
              className="absolute right-1 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
            >
              <Star
                className={`h-6 w-6 ${isFavorite(e.to) ? "fill-current text-amber-500" : ""}`}
              />
            </button>
          </li>
        ))}
        {visible.length === 0 && (
          <li className="card px-4 py-6 text-center text-sm text-muted-foreground">
            Ingen treff på «{query}».
          </li>
        )}
      </ul>
    </>
  );
}
